import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  FormHelperText,
  HStack,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Switch,
  Table,
  Tbody,
  Td,
  Textarea,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  SimpleGrid,
  Text,
  VStack,
  Tag,
  TagLabel,
  Tooltip,
  Badge,
  Wrap,
  WrapItem,
  useColorModeValue,
  Image as ChakraImage,
} from '@chakra-ui/react';
import { FiEdit2, FiPlus, FiTrash2, FiLink, FiGlobe } from 'react-icons/fi';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Event } from '../../types/event';
import { uploadFile } from '../../services/articles';
import { createEvent, deleteEvent, getEvents, updateEvent } from '../../services/eventService';
import { api } from '../../services/api';
// Removed react-datepicker to prevent crash; using native date/time inputs instead
import { getPublicSettings } from '../../services/settings';
import PollLinker from '../../components/admin/PollLinker';
import { useAuth } from '../../contexts/AuthContext';
import FilePreview from '../../components/common/FilePreview';
import { facrApi } from '../../services/facr/facrApi';
import { getCompetitionAliasesPublic } from '../../services/competitionAliases';
import { BlogTranslator } from '../../components/admin/BlogTranslator';
import { translateBlogContent, detectLanguage } from '../../services/translation';
import { UniversalTranslator } from '../../components/admin/UniversalTranslator';
import MapLinkImporter from '../../components/admin/MapLinkImporter';
import { MapCoordinates } from '../../utils/mapUrlParser';
import ContactMap from '../../components/home/ContactMap';
import RichTextEditor from '../../components/common/RichTextEditor';
import { getCachedYouTube, YouTubeVideo } from '../../services/youtube';
import SaveStatusIndicator from '../../components/common/SaveStatusIndicator';
import { useAutoSave, loadDraft, getDraftMetadata } from '../../hooks/useAutoSave';
import { FiVideo, FiYoutube } from 'react-icons/fi';
import ThumbnailPreview from '../../components/common/ThumbnailPreview';
import { assetUrl } from '../../utils/url';
import { createShortLink } from '../../services/shortlinks';
import DateRangePicker from '../../components/common/DateRangePicker';
import { HelpTooltipCard } from '../../components/common/HelpTooltipCard';
import UploadPanel, { UploadPanelFile } from '../../components/common/UploadPanel';
import AIPromptInput from '../../components/common/AIPromptInput';
import { AITextModelId, getDefaultTextModelId } from '../../utils/aiModels';
import { generateMainImageAI } from '../../services/ai';
import { processAIContent, cleanAIText } from '../../utils/aiTextProcessing';
import AILoadingModal from '../../components/common/AILoadingModal';

const types: Array<{ value: Event['type']; label: string }> = [
  { value: 'match', label: 'Zápas' },
  { value: 'training', label: 'Trénink' },
  { value: 'meeting', label: 'Schůzka' },
  { value: 'other', label: 'Jiné' },
];

const AdminActivitiesPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = (user as any)?.role === 'admin';
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const inputBg = useColorModeValue('white', 'gray.700');
  const textSecondary = useColorModeValue('gray.600', 'gray.400');
  const toast = useToast();
  const qc = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editing, setEditing] = useState<Partial<Event> | null>(null);
  const [draftKey, setDraftKey] = useState<string>('');
  const [localDraft, setLocalDraft] = useState<Partial<Event> | null>(null);
  const [aiPrompt, setAiPrompt] = useState<string>('');
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const [aiProgress, setAiProgress] = useState<number | undefined>(undefined);
  const [aiStartTime, setAiStartTime] = useState<number | null>(null);
  const [aiTone, setAiTone] = useState<'informative'|'friendly'|'formal'>('informative');
  const [aiOverwrite, setAiOverwrite] = useState<boolean>(true);
  const [aiModelId, setAiModelId] = useState<AITextModelId>(getDefaultTextModelId());
  // Location coordinates for map preview
  const [locationLat, setLocationLat] = useState<number | undefined>(undefined);
  const [locationLng, setLocationLng] = useState<number | undefined>(undefined);
  // YouTube videos from club channel
  const [clubVideos, setClubVideos] = useState<YouTubeVideo[]>([]);
  const [youtubeTab, setYoutubeTab] = useState<'club' | 'custom'>('club');
  const [savedLocations, setSavedLocations] = useState<Array<{ id: string; label: string; address: string; lat?: number; lng?: number }>>([]);
  const [selectedSavedId, setSelectedSavedId] = useState<string>('');
  const [mainImageLoading, setMainImageLoading] = useState<boolean>(false);

  // Auto-save hook - saves draft automatically
  const { saveStatus, lastSaved, forceSave, clearDraft } = useAutoSave({
    data: editing || {},
    storageKey: draftKey,
    onSave: async (data) => {
      // If event has ID, update it
      if (data.id) {
        try {
          return await updateEvent(data.id, data);
        } catch (e: any) {
          const status = e?.response?.status;
          if (status === 404) {
            if (data.title?.trim() && data.start_time) {
              const created = await createEvent(data);
              if (created?.id) {
                setEditing(prev => ({ ...prev, id: created.id } as any));
                setDraftKey(`draft-activity-${created.id}`);
                try { localStorage.removeItem('draft-activity-new'); } catch {}
              }
              return created;
            }
          }
          throw e;
        }
      }
      // If no ID and has minimal required fields, create as draft
      if (data.title?.trim() && data.start_time) {
        const created = await createEvent(data);
        if (created?.id) {
          setEditing(prev => ({ ...prev, id: created.id } as any));
          setDraftKey(`draft-activity-${created.id}`);
          try { localStorage.removeItem('draft-activity-new'); } catch {}
        }
        return created;
      }
      // Don't save if no title or start time
      return {};
    },
    debounceMs: 2000,
    enabled: isOpen && editing !== null,
  });

  // Load local new-draft and expose in list (no popup)
  const refreshLocalDraft = React.useCallback(() => {
    try {
      const key = 'draft-activity-new';
      const metadata = getDraftMetadata(key);
      if (metadata && metadata.age < 1440) {
        const d = loadDraft<Partial<Event>>(key);
        if (d) {
          const restored: any = { ...d };
          if (restored.id) delete restored.id;
          setLocalDraft(restored);
          return;
        }
      }
    } catch {}
    setLocalDraft(null);
  }, []);

  React.useEffect(() => {
    refreshLocalDraft();
  }, [refreshLocalDraft]);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-events'],
    queryFn: () => getEvents(),
  });
  const events = data || [];

  const [listDateFrom, setListDateFrom] = useState<string>('');
  const [listDateTo, setListDateTo] = useState<string>('');

  const filteredEvents = React.useMemo(() => {
    return events.filter((ev: Event) => {
      const raw: any = ev.start_time as any;
      const ts = raw ? new Date(raw).getTime() : NaN;
      if (isNaN(ts)) return true;
      if (listDateFrom) {
        const fromTs = new Date(listDateFrom + 'T00:00:00').getTime();
        if (!isNaN(fromTs) && ts < fromTs) return false;
      }
      if (listDateTo) {
        const toTs = new Date(listDateTo + 'T23:59:59').getTime();
        if (!isNaN(toTs) && ts > toTs) return false;
      }
      return true;
    });
  }, [events, listDateFrom, listDateTo]);

  // Localized label for event type
  const typeLabel = (t?: string) => {
    const v = String(t || '').trim() as any;
    const found = types.find((x) => x.value === v);
    return found ? found.label : 'Jiné';
  };

  const handleGenerateMainImage = async () => {
    if (!editing || !editing.title || !editing.title.trim()) {
      toast({ title: 'Chybí název aktivity', description: 'Nejprve zadejte název, aby AI věděla, co má zobrazit.', status: 'warning', duration: 4000 });
      return;
    }
    try {
      setMainImageLoading(true);
      const categoryName = (editing as any)?.category_name || '';
      const clubName = (settingsQ.data as any)?.club_name || '';
      const primaryColor = (settingsQ.data as any)?.primary_color || '';
      const secondaryColor = (settingsQ.data as any)?.secondary_color || '';
      const resp = await generateMainImageAI({
        subject: 'event',
        title: editing.title as string,
        category: categoryName,
        club_name: clubName,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
      });
      if (!resp || !resp.url) {
        toast({ title: 'AI nevrátila obrázek', description: 'Zkuste to prosím znovu nebo nastavte obrázek ručně.', status: 'warning', duration: 5000 });
        return;
      }
      setEditing((prev) => ({ ...(prev || {}), image_url: resp.url } as any));
      toast({ title: 'Obrázek aktivity vygenerován', description: 'Obrázek byl nastaven k aktivitě.', status: 'success', duration: 4000 });
    } catch (e: any) {
      toast({ title: 'Generování obrázku selhalo', description: e?.response?.data?.error || e?.message || 'Zkuste to prosím znovu.', status: 'error', duration: 5000 });
    } finally {
      setMainImageLoading(false);
    }
  };

  // Load club YouTube videos
  useEffect(() => {
    (async () => {
      try {
        const ytData = await getCachedYouTube();
        if (ytData?.videos) {
          setClubVideos(ytData.videos.slice(0, 20)); // Limit to 20 most recent
        }
      } catch (err) {
        console.error('Failed to load YouTube videos:', err);
      }
    })();
  }, []);

  // Settings for logo fetch (for placeholder image)
  const settingsQ = useQuery({
    queryKey: ['public-settings'],
    queryFn: getPublicSettings,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    try {
      const raw = localStorage.getItem('admin_saved_locations');
      const base: Array<{ id: string; label: string; address: string; lat?: number; lng?: number }> = raw ? JSON.parse(raw) : [];
      const s: any = settingsQ.data || {};
      const clubAddrParts = [s.contact_address, s.contact_city, s.contact_zip].filter((x: any) => String(x || '').trim());
      const clubAddr = clubAddrParts.join(', ');
      const hasCoords = typeof s.location_latitude === 'number' && typeof s.location_longitude === 'number' && !isNaN(s.location_latitude) && !isNaN(s.location_longitude);
      const label = s.club_name ? `Klub – ${s.club_name}` : 'Klub – Hlavní místo';
      if (clubAddr || hasCoords) {
        const exists = base.some((it) => (clubAddr && it.address === clubAddr) || (hasCoords && it.lat === s.location_latitude && it.lng === s.location_longitude));
        if (!exists) {
          base.unshift({ id: 'club-main', label, address: clubAddr || (s.contact_city || 'Klub'), lat: hasCoords ? s.location_latitude : undefined, lng: hasCoords ? s.location_longitude : undefined });
        }
      }
      setSavedLocations(base);
    } catch {
      setSavedLocations([]);
    }
  }, [settingsQ.data]);

  const persistSavedLocations = (list: Array<{ id: string; label: string; address: string; lat?: number; lng?: number }>) => {
    try { localStorage.setItem('admin_saved_locations', JSON.stringify(list)); } catch {}
    setSavedLocations(list);
  };

  const addCurrentLocationToSaved = () => {
    const address = String(editing?.location || '').trim();
    const hasCoords = typeof locationLat === 'number' || typeof locationLng === 'number';
    if (!address && !hasCoords) {
      toast({ title: 'Nelze uložit místo', description: 'Zadejte název/adresu nebo vyberte souřadnice.', status: 'warning' });
      return;
    }
    const id = String(Date.now());
    const label = address || 'Uložené místo';
    const next = [...savedLocations, { id, label, address, lat: locationLat, lng: locationLng }];
    persistSavedLocations(next);
    setSelectedSavedId(id);
    toast({ title: 'Místo uloženo', status: 'success', duration: 2000 });
  };

  const applySavedLocation = (id: string) => {
    setSelectedSavedId(id);
    const item = savedLocations.find((x) => x.id === id);
    if (!item) return;
    setLocationLat(item.lat);
    setLocationLng(item.lng);
    setEditing(prev => ({ ...(prev || {}), location: item.address, latitude: item.lat as any, longitude: item.lng as any } as any));
    toast({ title: 'Místo vybráno', description: item.label, status: 'success', duration: 1500 });
  };

  const deleteSelectedSaved = () => {
    if (!selectedSavedId) return;
    if (selectedSavedId === 'club-main') { toast({ title: 'Nelze smazat klubové místo', status: 'info' }); return; }
    const next = savedLocations.filter((x) => x.id !== selectedSavedId);
    persistSavedLocations(next);
    setSelectedSavedId('');
    toast({ title: 'Uložené místo smazáno', status: 'success', duration: 1500 });
  };

  const openCreate = () => {
    const key = 'draft-activity-new';
    setDraftKey(key);
    if (localDraft) {
      setEditing(localDraft);
    } else {
      setEditing({ title: '', description: '', type: 'other', is_public: true } as any);
    }
    setLocationLat(undefined);
    setLocationLng(undefined);
    onOpen();
  };
  const openEdit = (ev: Event) => {
    // Set unique draft key for this event
    const key = `draft-activity-${ev.id}`;
    setDraftKey(key);
    
    setEditing({ ...ev });
    // Initialize map coordinates from event
    if ((ev as any).latitude && (ev as any).longitude) {
      setLocationLat((ev as any).latitude);
      setLocationLng((ev as any).longitude);
    } else {
      setLocationLat(undefined);
      setLocationLng(undefined);
    }
    onOpen();
  };
  const closeModal = () => {
    setEditing(null);
    setLocationLat(undefined);
    setLocationLng(undefined);
    onClose();
    refreshLocalDraft();
  };


  const createMut = useMutation({
    mutationFn: (payload: Partial<Event>) => createEvent(payload),
    onSuccess: () => { toast({ title: 'Událost vytvořena', status: 'success' }); qc.invalidateQueries({ queryKey: ['admin-events'] }); closeModal(); },
    onError: (e: any) => toast({ title: 'Chyba při vytváření', description: e?.message || 'Došlo k chybě', status: 'error' }),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, payload }: { id: number|string; payload: Partial<Event> }) => updateEvent(id, payload),
    onSuccess: () => { toast({ title: 'Událost upravena', status: 'success' }); qc.invalidateQueries({ queryKey: ['admin-events'] }); closeModal(); },
    onError: (e: any) => toast({ title: 'Chyba při ukládání', description: e?.message || 'Došlo k chybě', status: 'error' }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteEvent(id),
    onSuccess: () => { toast({ title: 'Smazáno', status: 'success' }); qc.invalidateQueries({ queryKey: ['admin-events'] }); },
    onError: (e: any) => toast({ title: 'Smazání selhalo', description: e?.message || 'Došlo k chybě', status: 'error' }),
  });

  // Competitions list for category selection (like Articles)
  const [competitions, setCompetitions] = useState<Array<{ code?: string; name: string }>>([]);
  const [aliasesMap, setAliasesMap] = useState<Record<string, string>>({});
  useEffect(() => {
    (async () => {
      try {
        const settings = await getPublicSettings();
        const clubId = (settings as any)?.club_id || '';
        const clubType = ((settings as any)?.club_type || 'football') as 'football' | 'futsal';
        const comps: Array<{ code?: string; name: string }> = [];
        if (clubId) {
          try {
            const club = await facrApi.getClub(String(clubId), clubType);
            const arr = Array.isArray((club as any)?.competitions) ? (club as any).competitions : [];
            arr.forEach((c: any) => comps.push({ code: c.code, name: c.name || c.code }));
          } catch {}
        }
        let amap: Record<string, string> = {};
        try {
          const list = await getCompetitionAliasesPublic();
          list.forEach((a) => { if (a.code && a.alias) amap[a.code] = a.alias; });
        } catch {}
        const withAliases = comps.map((c) => ({ code: c.code, name: (c.code && amap[c.code]) ? amap[c.code] : c.name }));
        setAliasesMap(amap);
        setCompetitions(withAliases);
      } catch {}
    })();
  }, []);

  // AI generation for activity title/description (uses blog generator to produce quality Czech copy)
  const generateWithAI = async () => {
    try {
      setAiLoading(true);
      setAiProgress(0);
      setAiStartTime(Date.now());
      
      // Start progress simulation
      const progressInterval = setInterval(() => {
        setAiProgress(prev => {
          if (prev === undefined || prev >= 95) return prev;
          return Math.min(prev + Math.random() * 15 + 5, 95);
        });
      }, 2000);
      
      const e = editing || {};
      const stripHtml = (s: string) => String(s || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      const lines: string[] = [];
      const clubName = String(settingsQ?.data?.club_name || '').trim();
      if (clubName) lines.push(`Klub: ${clubName}`);
      if (e.type) lines.push(`Typ: ${e.type}`);
      if (!aiOverwrite && e.description) {
        const plain = stripHtml(e.description as any);
        if (plain) lines.push(`Stávající text (pro kontext): ${plain}`);
      }
      const base = lines.join('\n');
      const toneText = aiTone === 'informative'
        ? 'neutrálním, věcným a stručným stylem (bez nadsázky)'
        : aiTone === 'formal'
        ? 'formálním a profesionálním stylem (bez příkras)'
        : 'přátelským, ale věcným a stručným stylem (bez nadsázky)';
      const safeUserPrompt = (aiPrompt || 'Napiš krátkou neutrální pozvánku na klubovou aktivitu.').trim();
      const constraints = 'Nevkládej datum ani místo (lokalitu) do textu. Neuváděj konkrétní čas nebo adresu. Vyhýbej se superlativům, hyperbolám a marketingovým frázím. Nepoužívej slova jako „neopakovatelný“, „epický“, „úchvatný“ apod. Preferuj 2–3 krátké odstavce NEBO stručný seznam s odrážkami. Používej HTML značky ul/li pro odrážky a strong pro zvýraznění. Bez nadpisů (nepoužívej H1/H2). Dbej na věcný a střízlivý tón.';
      const prompt = `${safeUserPrompt}\n\nPiš ${toneText}, česky, s důrazem na jasnost a pozvánku k účasti. ${constraints}\nCílová délka: 80–120 slov.\nDetaily:\n${base}`.trim();
      const { data } = await api.post('/ai/blog/generate', {
        prompt,
        audience: clubName ? `Fanoušci klubu ${clubName}, oznámení/pozvánka` : 'Fanoušci klubu, oznámení/pozvánka',
        min_words: 100,
        model: aiModelId,
      });
      
      clearInterval(progressInterval);
      setAiProgress(100);
      
      // Handle potential JSON string response from AI (defensive parsing)
      let parsedData = data;
      if (typeof data === 'string') {
        try {
          parsedData = JSON.parse(data);
        } catch {
          throw new Error('AI vrátila neplatný formát odpovědi');
        }
      }
      
      const title = cleanAIText(String(parsedData?.title || '').trim());
      const html = processAIContent(String(parsedData?.html || '').trim(), { cleanText: true, preserveHtmlTags: true });
      if (!title && !html) throw new Error('AI nevrátila obsah');
      
      setEditing(prev => ({
        ...(prev || {}),
        title: aiOverwrite ? (title || (prev?.title || '')) : (prev?.title || title || ''),
        description: aiOverwrite ? (html || (prev?.description || '')) : `${(prev?.description || '')}${(prev?.description ? '\n\n' : '')}${html}`,
      }));
      toast({ title: 'Vygenerováno pomocí AI', status: 'success', duration: 3000 });
    } catch (e: any) {
      console.error('AI generation error:', e);
      toast({ title: 'AI generování selhalo', description: e?.response?.data?.error || e?.message || 'Zkuste doplnit více detailů a opakovat.', status: 'error', duration: 5000 });
    } finally {
      setTimeout(() => {
        setAiLoading(false);
        setAiProgress(undefined);
        setAiStartTime(null);
      }, 1000); // Brief moment to show 100% completion
    }
  };


  // Quick translation handler for activities list
  const handleQuickTranslateActivity = async (activity: Event) => {
    if (!activity.title || !activity.description) {
      toast({
        title: 'Nelze přeložit',
        description: 'Aktivita musí mít název a popis.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      const sourceLang = detectLanguage(activity.title + ' ' + activity.description);
      const targetLang = sourceLang === 'cs' ? 'en' : 'cs';

      if (sourceLang === targetLang) {
        toast({
          title: 'Překlad není potřeba',
          description: 'Aktivita je již v cílovém jazyce.',
          status: 'info',
          duration: 3000,
        });
        return;
      }

      const result = await translateBlogContent(activity.title, activity.description, sourceLang, targetLang);
      
      // Update the activity with translated content
      await updateEvent(activity.id, {
        title: result.translatedTitle,
        description: result.translatedContent,
      });

      // Refresh the activities list
      qc.invalidateQueries({ queryKey: ['admin-events'] });
      qc.invalidateQueries({ queryKey: ['events'] });

      toast({
        title: 'Aktivita přeložena',
        description: `Aktivita byla přeložena z ${sourceLang === 'cs' ? 'češtiny' : 'angličtiny'} do ${targetLang === 'cs' ? 'češtiny' : 'angličtiny'}.`,
        status: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      toast({
        title: 'Chyba při překladu',
        description: error?.message || 'Nepodařilo se přeložit aktivitu.',
        status: 'error',
        duration: 3000,
      });
    }
  };
  const [startDate, setStartDate] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');

  // Sync segmented date/time when editing changes
  useEffect(() => {
    const s = editing?.start_time ? new Date(editing.start_time as any) : null;
    if (s) {
      const yyyy = s.getFullYear();
      const mm = String(s.getMonth() + 1).padStart(2, '0');
      const dd = String(s.getDate()).padStart(2, '0');
      const hh = String(s.getHours()).padStart(2, '0');
      const min = String(s.getMinutes()).padStart(2, '0');
      setStartDate(`${yyyy}-${mm}-${dd}`);
      setStartTime(`${hh}:${min}`);
    } else {
      setStartDate(''); setStartTime('');
    }
    const e = editing?.end_time ? new Date(editing.end_time as any) : null;
    if (e) {
      const yyyy = e.getFullYear();
      const mm = String(e.getMonth() + 1).padStart(2, '0');
      const dd = String(e.getDate()).padStart(2, '0');
      const hh = String(e.getHours()).padStart(2, '0');
      const min = String(e.getMinutes()).padStart(2, '0');
      setEndDate(`${yyyy}-${mm}-${dd}`);
      setEndTime(`${hh}:${min}`);
    } else {
      setEndDate(''); setEndTime('');
    }
  }, [editing?.start_time, editing?.end_time]);

  const toISO = (d?: string, t?: string) => {
    if (!d || !t) return null as any;
    const val = new Date(`${d}T${t}:00`);
    if (isNaN(val.getTime())) return null as any;
    return val.toISOString() as any;
  };

  const missingTitle = !String(editing?.title || '').trim();
  const missingStart = !startDate || !startTime;

  const ensurePlaceholderImage = async (): Promise<string | undefined> => {
    try {
      const logoUrl = String(settingsQ.data?.club_logo_url || '/dist/img/logo-club-empty.svg');
      const canvas = document.createElement('canvas');
      const W = 1200, H = 630;
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d');
      if (!ctx) return undefined;
      // White background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, W, H);
      // Border for visibility
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 6;
      ctx.strokeRect(3, 3, W - 6, H - 6);
      // Draw club logo centered
      const img = new Image();
      img.crossOrigin = 'anonymous';
      const loadImg = () => new Promise<HTMLImageElement>((resolve, reject) => {
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = logoUrl;
      });
      try { await loadImg(); } catch { /* fallback */ }
      const boxW = Math.round(W * 0.5);
      const boxH = Math.round(H * 0.5);
      const x = Math.round((W - boxW) / 2);
      const y = Math.round((H - boxH) / 2);
      try { ctx.drawImage(img, x, y, boxW, boxH); } catch {}
      // Export and upload
      const blob: Blob | null = await new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/jpeg', 0.92));
      if (!blob) return undefined;
      const file = new File([blob], 'activity-cover.jpg', { type: 'image/jpeg' });
      const res = await uploadFile(file);
      return (res as any)?.url || undefined;
    } catch { return undefined; }
  };

  const onSubmit = async () => {
    if (!editing) return;
    // Validation to avoid 400s
    if (missingTitle) { toast({ title: 'Zadejte název', status: 'warning' }); return; }
    if (missingStart) { toast({ title: 'Vyplňte datum a čas začátku', status: 'warning' }); return; }
    const startISO = toISO(startDate, startTime);
    const endISO = endDate && endTime ? toISO(endDate, endTime) : null;
    if (endISO && startISO && new Date(endISO).getTime() < new Date(startISO).getTime()) {
      toast({ title: 'Konec musí být po začátku', status: 'warning' });
      return;
    }

    const payload: Partial<Event> = {
      title: (editing.title || '').trim(),
      description: (editing.description || '').trim(),
      start_time: startISO as any,
      end_time: (endISO as any) || null,
      location: (editing.location || '').trim(),
      type: (editing.type || 'other') as any,
      is_public: true,
      image_url: (editing as any).image_url || undefined,
      file_url: (editing as any).file_url || undefined,
      category_name: (editing as any)?.category_name || undefined,
      attachments: Array.isArray((editing as any)?.attachments) ? ((editing as any).attachments as any[]).map((a: any) => ({ name: a.name, url: a.url, mime_type: a.mime_type, size: a.size })) : undefined,
      youtube_url: (editing as any)?.youtube_url || undefined,
      latitude: (editing as any)?.latitude || locationLat || undefined,
      longitude: (editing as any)?.longitude || locationLng || undefined,
    };

    if ((editing as any).id) {
      await updateMut.mutateAsync({ id: (editing as any).id, payload });
    } else {
      await createMut.mutateAsync(payload);
    }
  };

  const uploadAttachmentFile = async (file: File): Promise<UploadPanelFile> => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'text/plain',
      'application/zip',
      'application/x-zip-compressed',
      'application/x-rar-compressed',
      'application/vnd.rar',
    ];
    const isAllowed =
      allowedTypes.includes(file.type) ||
      !!file.name.match(/\.(pdf|docx?|xlsx?|pptx?|jpe?g|png|gif|webp|txt|zip|rar)$/i);
    if (!isAllowed) {
      throw new Error(`Soubor "${file.name}" nelze nahrát (nepodporovaný formát).`);
    }
    const res = await uploadFile(file);
    return {
      url: (res as any).url,
      name: file.name,
      type: file.type || (res as any).type || 'application/octet-stream',
      size: file.size || (res as any).size || 0,
    };
  };

  return (
    <AdminLayout requireAdmin={false}>
      <Box>
        <HStack justify="space-between" mb={4}>
          <Heading size="lg">Aktivity (Události)</Heading>
          <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={openCreate}>Nová aktivita</Button>
        </HStack>
        <Wrap mb={4} spacing={3} align="center">
          <WrapItem>
            <DateRangePicker
              from={listDateFrom || undefined}
              to={listDateTo || undefined}
              onChange={(fromValue, toValue) => {
                setListDateFrom(fromValue);
                setListDateTo(toValue);
              }}
              size="sm"
            />
          </WrapItem>
          {(listDateFrom || listDateTo) && (
            <WrapItem>
              <Button
                size="sm"
                variant="outline"
                colorScheme="red"
                onClick={() => { setListDateFrom(''); setListDateTo(''); }}
                borderRadius="md"
              >
                Vymazat filtr
              </Button>
            </WrapItem>
          )}
        </Wrap>
        <Box bg={cardBg} borderWidth="1px" borderRadius="lg" overflowX="auto" boxShadow="sm" mb={6}>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Náhled</Th>
                <Th>Název</Th>
                <Th>Typ</Th>
                <Th>Začátek</Th>
                <Th>Konec</Th>
                <Th>Místo</Th>
                <Th w="140px">Akce</Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading && (
                <Tr><Td colSpan={8}>Načítání…</Td></Tr>
              )}
              {!isLoading && localDraft && (
                <Tr key="local-draft" opacity={0.6}>
                  <Td>
                    {(localDraft as any).image_url ? (
                      <ThumbnailPreview 
                        src={assetUrl((localDraft as any).image_url) || (localDraft as any).image_url}
                        alt={(localDraft as any).title || 'Koncept'}
                        size="48px"
                        previewSize="350px"
                      />
                    ) : (
                      <ChakraImage 
                        src={assetUrl(settingsQ.data?.club_logo_url) || assetUrl('/dist/img/logo-club-empty.svg') || '/dist/img/logo-club-empty.svg'}
                        alt="No image"
                        boxSize="48px"
                        objectFit="contain"
                        opacity={0.3}
                      />
                    )}
                  </Td>
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="medium">{(localDraft as any).title || 'Bez názvu (koncept)'}</Text>
                      <Text fontSize="xs" color="gray.500">Koncept (lokálně uložený)</Text>
                    </VStack>
                  </Td>
                  <Td>
                    <Badge colorScheme="gray" fontSize="xs">{(localDraft as any).type ? typeLabel((localDraft as any).type as any) : '—'}</Badge>
                  </Td>
                  <Td>—</Td>
                  <Td>—</Td>
                  <Td>{(localDraft as any).location || '—'}</Td>
                  <Td><Badge colorScheme="gray">Koncept</Badge></Td>
                  <Td isNumeric>
                    <HStack spacing={1} justify="flex-end">
                      <IconButton aria-label="Upravit koncept" size="sm" icon={<FiEdit2 />} onClick={openCreate} />
                      <IconButton 
                        aria-label="Smazat koncept" 
                        size="sm" 
                        colorScheme="red" 
                        icon={<FiTrash2 />} 
                        onClick={() => { try { localStorage.removeItem('draft-activity-new'); } catch {} setLocalDraft(null); toast({ title: 'Koncept odstraněn', status: 'success', duration: 2000 }); }} 
                      />
                    </HStack>
                  </Td>
                </Tr>
              )}
              {!isLoading && filteredEvents.map(ev => (
                <Tr key={ev.id}>
                  <Td>
                    {(ev as any).image_url ? (
                      <ThumbnailPreview 
                        src={assetUrl((ev as any).image_url) || (ev as any).image_url} 
                        alt={ev.title} 
                        size="48px"
                        previewSize="350px"
                      />
                    ) : (
                      <ChakraImage 
                        src={assetUrl(settingsQ.data?.club_logo_url) || assetUrl('/dist/img/logo-club-empty.svg') || '/dist/img/logo-club-empty.svg'} 
                        alt="No image" 
                        boxSize="48px" 
                        objectFit="contain" 
                        opacity={0.3}
                      />
                    )}
                  </Td>
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="medium">{ev.title}</Text>
                      <Text fontSize="xs" color="gray.500">{ev.is_public ? '✓ Veřejná' : '○ Koncept'}</Text>
                    </VStack>
                  </Td>
                  <Td>{typeLabel(ev.type as any)}</Td>
                  <Td>{new Date(ev.start_time).toLocaleString()}</Td>
                  <Td>{ev.end_time ? new Date(ev.end_time).toLocaleString() : '-'}</Td>
                  <Td>{ev.location || '-'}</Td>
                  <Td>
                    <HStack>
                      <IconButton aria-label="Upravit" size="sm" icon={<FiEdit2 />} onClick={() => openEdit(ev)} />
                      <IconButton 
                        aria-label="Přeložit" 
                        size="sm" 
                        colorScheme="blue" 
                        icon={<FiGlobe />} 
                        title="Přeložit aktivitu"
                        onClick={() => handleQuickTranslateActivity(ev)} 
                      />
                      <IconButton aria-label="Smazat" size="sm" colorScheme="red" icon={<FiTrash2 />} onClick={() => deleteMut.mutate(ev.id)} />
                      <IconButton
                        aria-label="Zkrátit odkaz"
                        size="sm"
                        icon={<FiLink />}
                        title="Zkrátit odkaz pro sdílení"
                        onClick={async () => {
                          try {
                            const origin = window.location.origin;
                            const target = `${origin}/aktivita/${ev.id}`;
                            const res = await createShortLink({ target_url: target, title: ev.title, source_type: 'event', source_id: ev.id as any, code: `e${ev.id}` });
                            await navigator.clipboard.writeText(res.short_url);
                            toast({ title: 'Zkrácený odkaz zkopírován', description: res.short_url, status: 'success', duration: 4000 });
                          } catch (e: any) {
                            toast({ title: 'Vytvoření odkazu selhalo', description: e?.message || 'Zkuste to znovu', status: 'error' });
                          }
                        }}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        {/* Modal */}
        <Modal isOpen={isOpen} onClose={closeModal} isCentered scrollBehavior="inside">
          <ModalOverlay backdropFilter="blur(3px)" />
          <ModalContent maxW={{ base: '96vw', md: '920px' }} maxH={{ base: '90vh', md: '86vh' }} borderRadius="2xl" overflow="hidden" boxShadow="2xl">
            <ModalHeader>
              <HStack justify="space-between" align="start" w="full" pr={8}>
                <VStack align="start" spacing={1} flex={1}>
                  <Heading size="md">{(editing as any)?.id ? 'Upravit aktivitu' : 'Nová aktivita'}</Heading>
                  <Text fontSize="sm" color="gray.500">Plánujte klubové akce, sdílejte s fanoušky a týmem.</Text>
                </VStack>
                <SaveStatusIndicator status={saveStatus} lastSaved={lastSaved} compact />
              </HStack>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody overflowY="auto" maxH={{ base: '76vh', md: '70vh' }}>
              <Box borderWidth="1px" borderRadius="lg" p={4} mb={5} bg={useColorModeValue('gray.50', 'gray.900')}>
                <HStack justify="space-between" align="start" mb={2}>
                  <Heading size="sm">AI generování</Heading>
                  <HStack spacing={2} align="center">
                    <Badge colorScheme="purple" variant="subtle">Beta</Badge>
                    <HelpTooltipCard
                      label="Jak AI píše text"
                      title="Jak funguje AI generování popisu"
                      items={[
                        'AI pomáhá s titulkem a popisem aktivity, ale nemění datum, čas ani místo – ty nastavujete ručně.',
                        'Do promptu uveďte typ akce, cílovou skupinu a důležité body programu. AI použije klubové informace z nastavení.',
                        'Přepínač „Přepsat existující obsah" určuje, zda AI může přepsat už vyplněný titulek a popis, nebo je jen doplní.',
                      ]}
                    />
                  </HStack>
                </HStack>
                <Text fontSize="sm" color="gray.600" mb={2}>Zadejte instrukce pro AI nebo klikněte na předlohy níže. AI může vytvořit nebo doplnit titulek a popis události.</Text>
                <AIPromptInput
                  value={aiPrompt}
                  onChange={setAiPrompt}
                  onSubmit={(_, modelId) => {
                    setAiModelId(modelId);
                    return generateWithAI();
                  }}
                  isSubmitting={aiLoading}
                  placeholder="Např.: Trénink A-týmu, hřiště TJ Dvorce, 18:00–19:30, pro všechny hráče."
                  helperText="AI popíše aktivitu bez data, času a přesného místa."
                />
                <Wrap spacing={2} mt={2}>
                  {[
                    'Pozvánka na trénink se zaměřením na kondici',
                    'Oznámení přátelského zápasu pro fanoušky',
                    'Rodičovská schůzka mládeže – stručný program',
                  ].map((t, i) => (
                    <WrapItem key={i}>
                      <Tag size="sm" variant="subtle" colorScheme="blue" _hover={{ cursor: 'pointer', opacity: 0.9 }} onClick={() => setAiPrompt(t)}>
                        <TagLabel>{t}</TagLabel>
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
                <HStack mt={3} spacing={3} align="center">
                  <FormControl maxW="220px">
                    <FormLabel mb={1}>Tón</FormLabel>
                    <Select size="sm" value={aiTone} onChange={(e)=> setAiTone(e.target.value as any)}>
                      <option value="friendly">Přátelský</option>
                      <option value="informative">Informační</option>
                      <option value="formal">Formální</option>
                    </Select>
                  </FormControl>
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0">Přepsat existující obsah</FormLabel>
                    <Switch isChecked={aiOverwrite} onChange={(e)=> setAiOverwrite(e.target.checked)} />
                  </FormControl>
                  <Tooltip label="AI doplní titul a popis podle zadaných informací." hasArrow>
                    <Button
                      size="sm"
                      onClick={generateWithAI}
                      isLoading={aiLoading}
                      leftIcon={<FiPlus size={14} />}
                      bg="brand.primary"
                      color="text.onPrimary"
                      _hover={{ filter: 'brightness(0.95)' }}
                      whiteSpace="nowrap"
                    >
                      AI text
                    </Button>
                  </Tooltip>
                </HStack>
              </Box>
              <FormControl isRequired mb={3}>
                <FormLabel>Název</FormLabel>
                <Input value={editing?.title || ''} onChange={(e) => setEditing(prev => ({ ...(prev || {}), title: e.target.value }))} />
                {missingTitle && (
                  <Text fontSize="sm" color="orange.500" mt={1}>Vyplňte název aktivity.</Text>
                )}
              </FormControl>

              <FormControl mb={3}>
                <FormLabel>Popis (Rich Text Editor)</FormLabel>
                <RichTextEditor
                  value={editing?.description || ''}
                  onChange={(value) => setEditing(prev => ({ ...(prev || {}), description: value }))}
                  height="300px"
                  toolbar="full"
                  showImageResize={true}
                  placeholder="Zadejte popis události..."
                />
              </FormControl>

              {/* Activity Translation */}
              {editing?.title && editing?.description && (
                <UniversalTranslator
                  title={editing.title}
                  content={editing.description}
                  contentType="activity"
                  onTranslationComplete={(translatedTitle, translatedContent) => {
                    setEditing((prev) => ({
                      ...(prev as any),
                      title: translatedTitle,
                      description: translatedContent,
                    }));
                    toast({
                      title: 'Aktivita přeložena',
                      description: 'Aktivita byla úspěšně přeložena a uložena do formuláře.',
                      status: 'success',
                      duration: 3000,
                    });
                  }}
                />
              )}

              <Box mb={3}>
                <FormLabel>YouTube Video (volitelné)</FormLabel>
                <Box borderWidth="1px" borderRadius="md" p={3} bg={useColorModeValue('gray.50', 'gray.900')}>
                  <HStack mb={3} spacing={2}>
                    <Button
                      size="sm"
                      leftIcon={<FiYoutube />}
                      onClick={() => setYoutubeTab('club')}
                      variant={youtubeTab === 'club' ? 'solid' : 'outline'}
                      colorScheme={youtubeTab === 'club' ? 'red' : 'gray'}
                    >
                      Z kanálu klubu ({clubVideos.length})
                    </Button>
                    <Button
                      size="sm"
                      leftIcon={<FiLink />}
                      onClick={() => setYoutubeTab('custom')}
                      variant={youtubeTab === 'custom' ? 'solid' : 'outline'}
                      colorScheme={youtubeTab === 'custom' ? 'blue' : 'gray'}
                    >
                      Vlastní odkaz
                    </Button>
                  </HStack>

                  {youtubeTab === 'club' && (
                    <Box>
                      {clubVideos.length === 0 ? (
                        <Text fontSize="sm" color="gray.500">
                          Žádná videa z kanálu klubu. Nastavte YouTube kanál v Nastavení.
                        </Text>
                      ) : (
                        <VStack align="stretch" spacing={2} maxH="300px" overflowY="auto">
                          {clubVideos.map((video) => {
                            const videoUrl = `https://www.youtube.com/watch?v=${video.video_id}`;
                            const isSelected = (editing as any)?.youtube_url?.includes(video.video_id);
                            return (
                              <HStack
                                key={video.video_id}
                                p={2}
                                borderWidth="1px"
                                borderRadius="md"
                                borderColor={isSelected ? 'red.500' : borderColor}
                                bg={isSelected ? useColorModeValue('red.50', 'red.900') : cardBg}
                                cursor="pointer"
                                _hover={{ borderColor: 'red.300', bg: useColorModeValue('red.50', 'red.900') }}
                                onClick={() => {
                                  setEditing(prev => ({ ...(prev || {}), youtube_url: videoUrl } as any));
                                  toast({
                                    title: 'Video vybráno',
                                    description: video.title,
                                    status: 'success',
                                    duration: 2000,
                                  });
                                }}
                              >
                                <ChakraImage
                                  src={video.thumbnail_url}
                                  alt={video.title}
                                  boxSize="60px"
                                  objectFit="cover"
                                  borderRadius="md"
                                />
                                <VStack align="start" flex={1} spacing={0}>
                                  <Text fontSize="sm" fontWeight="medium" noOfLines={2}>
                                    {video.title}
                                  </Text>
                                  <HStack fontSize="xs" color="gray.500">
                                    {video.published_text && <Text>{video.published_text}</Text>}
                                    {video.views_text && <Text>• {video.views_text}</Text>}
                                  </HStack>
                                </VStack>
                                {isSelected && (
                                  <Badge colorScheme="red">Vybráno</Badge>
                                )}
                              </HStack>
                            );
                          })}
                        </VStack>
                      )}
                    </Box>
                  )}

                  {youtubeTab === 'custom' && (
                    <VStack align="stretch" spacing={2}>
                      <Input 
                        value={(editing as any)?.youtube_url || ''} 
                        onChange={(e) => setEditing(prev => ({ ...(prev || {}), youtube_url: e.target.value } as any))} 
                        placeholder="https://www.youtube.com/watch?v=... nebo https://youtu.be/..."
                      />
                      <Text fontSize="xs" color="gray.500">
                        Vložte odkaz na jakékoliv YouTube video (nemusí být z vašeho kanálu).
                      </Text>
                    </VStack>
                  )}
                  
                  {(editing as any)?.youtube_url && (
                    <HStack mt={2} spacing={2}>
                      <Badge colorScheme="green" fontSize="xs">Video nastaveno</Badge>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => setEditing(prev => ({ ...(prev || {}), youtube_url: '' } as any))}
                      >
                        Zrušit video
                      </Button>
                    </HStack>
                  )}
                </Box>
              </Box>
              <Box mt={3}>
                <Text fontWeight="bold" mb={2}>Datum a čas</Text>
                <DateRangePicker
                  from={startDate || undefined}
                  to={endDate || undefined}
                  onChange={(fromValue, toValue) => {
                    setStartDate(fromValue || '');
                    setEndDate(toValue || '');
                    // Update start_time when from changes
                    if (fromValue && startTime) {
                      const iso = toISO(fromValue, startTime);
                      setEditing(prev => ({ ...(prev || {}), start_time: iso || (prev as any)?.start_time }));
                    }
                    // Update end_time when to changes
                    if (toValue && endTime) {
                      const iso = toISO(toValue, endTime);
                      setEditing(prev => ({ ...(prev || {}), end_time: iso as any }));
                    }
                  }}
                  size="sm"
                />
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mt={3}>
                  <FormControl isRequired>
                    <FormLabel>Čas začátku</FormLabel>
                    <Input type="time" step="900" value={startTime} onChange={(e) => {
                      const v = e.target.value; setStartTime(v);
                      if (startDate) {
                        const iso = toISO(startDate, v);
                        // Auto-shift end if needed
                        setEditing(prev => {
                          const nextStart = iso;
                          const prevEnd = prev?.end_time ? new Date(prev.end_time as any) : null;
                          let nextEndISO = prev?.end_time as any;
                          if (prevEnd && nextStart && prevEnd.getTime() < new Date(nextStart).getTime()) {
                            nextEndISO = new Date(new Date(nextStart).getTime() + 60*60*1000).toISOString() as any;
                          }
                          return ({ ...(prev || {}), start_time: nextStart, end_time: nextEndISO });
                        });
                      }
                    }} />
                    {missingStart && (
                      <Text fontSize="sm" color="orange.500" mt={1}>Vyplňte datum i čas začátku.</Text>
                    )}
                  </FormControl>
                  <FormControl>
                    <FormLabel>Čas konce (volitelné)</FormLabel>
                    <Input type="time" step="900" value={endTime} onChange={(e) => {
                      const v = e.target.value; setEndTime(v);
                      if (endDate) {
                        const iso = toISO(endDate, v);
                        setEditing(prev => ({ ...(prev || {}), end_time: iso as any }));
                      }
                    }} />
                    <HStack mt={2}>
                      <Button size="sm" variant="outline" onClick={() => setEditing(prev => {
                        if (!prev?.start_time) return prev || {};
                        const start = new Date(prev.start_time as any).getTime();
                        const end = new Date(start + 60*60*1000).toISOString();
                        return ({ ...(prev || {}), end_time: end });
                      })}>+60m</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(prev => {
                        if (!prev?.start_time) return prev || {};
                        const start = new Date(prev.start_time as any).getTime();
                        const end = new Date(start + 90*60*1000).toISOString();
                        return ({ ...(prev || {}), end_time: end });
                      })}>+90m</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(prev => {
                        if (!prev?.start_time) return prev || {};
                        const start = new Date(prev.start_time as any).getTime();
                        const end = new Date(start + 120*60*1000).toISOString();
                        return ({ ...(prev || {}), end_time: end });
                      })}>+120m</Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEndDate(''); setEndTime(''); setEditing(prev => ({ ...(prev || {}), end_time: null })); }}>Zrušit</Button>
                    </HStack>
                  </FormControl>
                </SimpleGrid>
                {/* Quick presets for start time */}
                <HStack mt={2} spacing={2}>
                  <Button size="sm" variant="outline" onClick={() => setEditing(prev => {
                    const now = new Date();
                    const m = now.getMinutes();
                    const rounded = new Date(now.getTime() + ((15 - (m % 15)) % 15) * 60000);
                    const yyyy = rounded.getFullYear();
                    const mm = String(rounded.getMonth() + 1).padStart(2, '0');
                    const dd = String(rounded.getDate()).padStart(2, '0');
                    const hh = String(rounded.getHours()).padStart(2, '0');
                    const min = String(rounded.getMinutes()).padStart(2, '0');
                    setStartDate(`${yyyy}-${mm}-${dd}`);
                    setStartTime(`${hh}:${min}`);
                    return ({ ...(prev || {}), start_time: rounded.toISOString() as any });
                  })}>Nyní (zaokrouhlit 15m)</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(prev => {
                    const d = new Date(); d.setHours(18,0,0,0);
                    const yyyy = d.getFullYear();
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const dd = String(d.getDate()).padStart(2, '0');
                    setStartDate(`${yyyy}-${mm}-${dd}`);
                    setStartTime(`18:00`);
                    return ({ ...(prev || {}), start_time: d.toISOString() as any });
                  })}>Dnes 18:00</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(prev => {
                    const d = new Date(); d.setDate(d.getDate()+1); d.setHours(18,0,0,0);
                    const yyyy = d.getFullYear();
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const dd = String(d.getDate()).padStart(2, '0');
                    setStartDate(`${yyyy}-${mm}-${dd}`);
                    setStartTime(`18:00`);
                    return ({ ...(prev || {}), start_time: d.toISOString() as any });
                  })}>Zítra 18:00</Button>
                  <Tooltip label="Rychlé nastavení zítřejšího rána" hasArrow>
                    <Button size="sm" variant="outline" onClick={() => setEditing(prev => {
                      const d = new Date(); d.setDate(d.getDate()+1); d.setHours(9,0,0,0);
                      const yyyy = d.getFullYear();
                      const mm = String(d.getMonth() + 1).padStart(2, '0');
                      const dd = String(d.getDate()).padStart(2, '0');
                      setStartDate(`${yyyy}-${mm}-${dd}`);
                      setStartTime(`09:00`);
                      return ({ ...(prev || {}), start_time: d.toISOString() as any });
                    })}>Zítra 9:00</Button>
                  </Tooltip>
                </HStack>
                <Text mt={2} fontSize="xs" color="gray.500">Časová zóna: {Intl.DateTimeFormat().resolvedOptions().timeZone}</Text>
              </Box>
            <Box mt={4}>
              <Heading size="sm" mb={3}>Místo konání</Heading>
              
              <Box bg={useColorModeValue('gray.50', 'gray.900')} p={4} borderRadius="md" borderWidth="1px" mb={3}>
                <FormControl>
                  <FormLabel fontSize="sm">Uložená místa (rychlý výběr)</FormLabel>
                  <HStack spacing={2} align="center">
                    <Select
                      size="sm"
                      placeholder="Vyberte uložené místo..."
                      value={selectedSavedId}
                      onChange={(e) => applySavedLocation(e.target.value)}
                      flex={1}
                    >
                      {savedLocations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.label}{loc.address ? ` — ${loc.address}` : ''}
                        </option>
                      ))}
                    </Select>
                    <Button size="sm" variant="outline" onClick={addCurrentLocationToSaved}>Uložit aktuální</Button>
                    <Button size="sm" variant="ghost" colorScheme="red" onClick={deleteSelectedSaved} isDisabled={!selectedSavedId || selectedSavedId === 'club-main'}>Smazat</Button>
                  </HStack>
                  <Text fontSize="xs" color={textSecondary} mt={1}>Vyberte klubové nebo dříve uložené místo. „Uložit aktuální“ přidá současný název/adresu a souřadnice.</Text>
                </FormControl>
              </Box>
              
              {/* MapLinkImporter */}
              <Box bg={useColorModeValue('gray.50', 'gray.900')} p={4} borderRadius="md" borderWidth="1px" mb={3}>
                <Text fontSize="sm" fontWeight="semibold" mb={2}>Importovat z odkazu na mapu</Text>
                <MapLinkImporter
                  currentLatitude={locationLat}
                  currentLongitude={locationLng}
                  mapStyle={settingsQ.data?.map_style || 'positron'}
                  clubPrimaryColor={settingsQ.data?.primary_color}
                  clubSecondaryColor={settingsQ.data?.accent_color}
                  clubName={editing?.title || editing?.location || 'Místo události'}
                  onImport={(coords: MapCoordinates) => {
                    setLocationLat(coords.latitude);
                    setLocationLng(coords.longitude);
                    
                    // Build location string from address data
                    let locationString = '';
                    if (coords.address) {
                      // Use full address if available
                      locationString = coords.address;
                    } else {
                      // Build address from parts
                      const parts: string[] = [];
                      if (coords.street) parts.push(coords.street);
                      if (coords.city) parts.push(coords.city);
                      if (coords.zip) parts.push(coords.zip);
                      locationString = parts.join(', ');
                    }
                    
                    // Save coordinates and location to the event
                    setEditing(prev => ({
                      ...(prev || {}),
                      latitude: coords.latitude,
                      longitude: coords.longitude,
                      location: locationString || prev?.location || '',
                    } as any));
                    
                    toast({
                      title: 'Místo importováno',
                      description: locationString || `Lat: ${coords.latitude.toFixed(6)}, Lng: ${coords.longitude.toFixed(6)}`,
                      status: 'success',
                      duration: 3000,
                    });
                  }}
                />
              </Box>

              <HStack spacing={3} align="start" mt={3}>
                <FormControl flex={2}>
                  <FormLabel>Název místa / Adresa</FormLabel>
                  <Input 
                    value={editing?.location || ''} 
                    onChange={(e) => setEditing(prev => ({ ...(prev || {}), location: e.target.value }))} 
                    placeholder="např. Sportovní hala TJ Sokol"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>Zobrazí se návštěvníkům (s možnou mapou)</Text>
                </FormControl>
                <FormControl flex={1}>
                  <FormLabel>Typ</FormLabel>
                  <Select value={(editing?.type as any) || 'other'} onChange={(e) => setEditing(prev => ({ ...(prev || {}), type: e.target.value as any }))}>
                    {types.map(t => (<option key={t.value} value={t.value}>{t.label}</option>))}
                  </Select>
                </FormControl>
              </HStack>
            </Box>
            <FormControl mt={3}>
              <FormLabel>Kategorie (soutěž)</FormLabel>
              <Select
                placeholder="Vyberte kategorii (volitelné)"
                value={(editing as any)?.category_name || ''}
                onChange={(e) => setEditing(prev => ({ ...(prev || {}), category_name: e.target.value } as any))}
              >
                {competitions.map((c, idx) => (
                  <option key={(c.code || c.name) + '_' + idx} value={(c.code && aliasesMap[c.code]) ? aliasesMap[c.code] : c.name}>
                    {(c.code && aliasesMap[c.code]) ? aliasesMap[c.code] : c.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* UploadPanel for activity cover image */}
            <FormControl mt={3}>
              <FormLabel>Obrázek/náhled</FormLabel>
              <VStack align="stretch" spacing={3}>
                <UploadPanel
                  label="Obrázek aktivity"
                  description="Nahrajte nebo přetáhněte obrázek/náhled pro aktivitu."
                  accept="image/*"
                  multiple={false}
                  maxFiles={1}
                  allowUrlImport
                  urlPlaceholder="/uploads/... nebo https://example.com/obrazek.jpg"
                  onChange={(files) => {
                    const last = files[files.length - 1];
                    setEditing((prev) => ({
                      ...(prev || {}),
                      image_url: last?.url || (prev as any)?.image_url || '',
                    } as any));
                  }}
                />
                <HStack align="center" spacing={3}>
                  <Box boxSize="80px" borderRadius="md" overflow="hidden" bg="gray.100">
                    {editing?.image_url ? (
                      <ThumbnailPreview
                        src={assetUrl(editing.image_url as any) || editing.image_url}
                        alt={editing.title || 'Náhled aktivity'}
                        size="80px"
                        previewSize="350px"
                      />
                    ) : (
                      <ChakraImage
                        src={assetUrl(settingsQ.data?.club_logo_url) || assetUrl('/dist/img/logo-club-empty.svg') || '/dist/img/logo-club-empty.svg'}
                        alt="No image"
                        boxSize="80px"
                        objectFit="contain"
                        opacity={0.4}
                      />
                    )}
                  </Box>
                  <Input
                    value={(editing as any)?.image_url || ''}
                    onChange={(e) => setEditing(prev => ({ ...(prev || {}), image_url: e.target.value } as any))}
                    placeholder="/uploads/...jpg"
                  />
                </HStack>
                <Text fontSize="xs" color="gray.500">
                  Doporučený poměr stran 16:9, minimálně 1200×675 px.
                </Text>
              </VStack>
            </FormControl>

            {/* Attachments */}
            <FormControl mt={4}>
              <FormLabel>Přílohy (dokumenty, obrázky, ZIP)</FormLabel>
              <FormHelperText mb={2}>
                Přidejte dokumenty k aktivitě (PDF, Word, Excel, PowerPoint, obrázky, ZIP). Přílohy se zobrazí pod textem události.
              </FormHelperText>
              <UploadPanel
                label="Přílohy aktivity"
                description="Nahrajte dokumenty, obrázky nebo archivy jako přílohy k aktivitě."
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.txt,.zip,.rar"
                multiple
                maxFiles={30}
                uploadFn={uploadAttachmentFile}
                onUploadFinished={(uploaded) => {
                  if (!uploaded || uploaded.length === 0) return;
                  setEditing((prev) => {
                    const prevAtts = Array.isArray((prev as any)?.attachments)
                      ? (prev as any).attachments
                      : [];
                    const mapped = uploaded.map((f) => ({
                      name: f.name,
                      url: f.url,
                      mime_type: f.type,
                      size: f.size,
                    }));
                    return {
                      ...(prev || {}),
                      attachments: [...prevAtts, ...mapped],
                    } as any;
                  });
                }}
              />
              <Box mt={2}>
                {Array.isArray((editing as any)?.attachments) && (editing as any).attachments.length > 0 ? (
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Název</Th>
                        <Th>Velikost</Th>
                        <Th>Akce</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {((editing as any).attachments as any[]).map((att: any, idx: number) => (
                        <Tr key={idx}>
                          <Td colSpan={3} p={2}>
                            <HStack justify="space-between" w="full">
                              <Box flex={1}>
                                <FilePreview
                                  url={att.url}
                                  name={att.name}
                                  mimeType={att.mime_type}
                                  size={att.size}
                                />
                              </Box>
                              <Button
                                size="xs"
                                variant="outline"
                                colorScheme="red"
                                flexShrink={0}
                                ml={2}
                                onClick={() =>
                                  setEditing((prev) => ({
                                    ...(prev as any),
                                    attachments: ((prev as any).attachments || []).filter((_: any, i: number) => i !== idx),
                                  }))
                                }
                              >
                                Odebrat
                              </Button>
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                ) : (
                  <Box color="gray.500">Žádné přílohy</Box>
                )}
              </Box>
            </FormControl>

            {/* Poll Section */}
            <Box mt={6} pt={4} borderTopWidth="1px" borderColor={borderColor}>
              <Heading size="sm" mb={3}>Anketa</Heading>
              {isAdmin && editing?.id ? (
                <PollLinker eventId={editing.id} />
              ) : (
                <Box bg={useColorModeValue('blue.50', 'blue.900')} p={4} borderRadius="md" borderWidth="1px" borderColor="blue.200">
                  <Text fontSize="sm" color={useColorModeValue('blue.700', 'blue.200')}>
                    💡 Nejprve uložte aktivitu, poté budete moci vytvořit nebo připojit anketu přímo zde.
                  </Text>
                </Box>
              )}
            </Box>
            </ModalBody>
            <ModalFooter>
              <HStack w="100%" justify="space-between">
                <Text fontSize="xs" color="gray.500">
                  Ukládáním souhlasíte s publikací podle nastavení „Veřejná“.
                </Text>
                <HStack>
                  <Button variant="ghost" mr={1} onClick={closeModal}>Zrušit</Button>
                  <Button bg="brand.primary" color="text.onPrimary" _hover={{ filter: 'brightness(0.95)' }} onClick={onSubmit} isLoading={createMut.isLoading || updateMut.isLoading}>Uložit</Button>
                </HStack>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>

        
        <AILoadingModal
          isOpen={aiLoading}
          onClose={() => {}} // Can't close while AI is working
          title="AI generuje aktivitu"
          message="AI pracuje na generování obsahu pro aktivitu..."
          progress={aiProgress}
          estimatedTime={aiStartTime ? 30 : undefined} // 30 seconds estimated
        />

      </Box>
    </AdminLayout>
  );
};

export default AdminActivitiesPage;
